import { BrannanWebsiteHeaderSettings } from '@apps/guest/websiteHeader';
import React, { useCallback } from 'react';
import PageOrScreenWidthImage from '../PageOrScreenWidthImage/PageOrScreenWidthImage';
import { EventPageType, WebsitePagePhotoPresentationFragment } from '@graphql/generated';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { AuxPhoto } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/components/AuxFrame/AuxFrame.types';
import { CenterPhoto } from '../CenterPhoto/CenterPhoto';
import { PhotoSlider, PhotoTrio } from '@apps/guest/packages/layout-engine/widgets/Custom/components';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';

interface HeaderPhotoProps {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageId?: string;
  pageSlug?: string;
  pageType?: EventPageType;
  handleEditPhotoClick?: () => void;
}

export const HeaderPhoto = ({ websiteHeader, pageId, pageSlug, pageType }: HeaderPhotoProps) => {
  const { headerPhoto } = websiteHeader;

  const handleEditPhotoClick = useCallback(
    (photo?: AuxPhoto, photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>) => {
      // send the message to the parent window to open the photo editor
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPhoto',
          inlineEditData: {
            enablePhotoLayout: true,
            photoPresentation,
            photo,
            pageId: pageId || '',
            page: pageType,
            pageSlug
          }
        }
      });
    },
    [pageId, pageSlug, pageType]
  );

  const getHeaderPhotoComponent = useCallback(() => {
    if (!headerPhoto) {
      return null;
    }

    switch (headerPhoto.presentationLayout) {
      case 'fullWidth':
        return (
          <PageOrScreenWidthImage variant="screen" pageId={pageId} photoDetails={headerPhoto} pageType={pageType} pageSlug={pageSlug} handleEditPhotoClick={handleEditPhotoClick} />
        );
      case 'pageWidth':
        return (
          <PageOrScreenWidthImage variant="page" pageId={pageId} photoDetails={headerPhoto} pageType={pageType} pageSlug={pageSlug} handleEditPhotoClick={handleEditPhotoClick} />
        );
      case 'center': {
        if (headerPhoto.photos?.[0]) {
          const photo = headerPhoto.photos[0];

          return <CenterPhoto photo={photo} />;
        }
        return null;
      }
      case 'photoSlider': {
        return (
          <PhotoSlider
            photos={headerPhoto?.photos?.map(photo => ({ ...photo, id: photo.id, photoUrl: photo.url })) || []}
            // We are passing aspect ratio, because the container and canvas uses same aspect ratio. Also to limit the container height with respect to the aspect ratio
            containerAspectRatio={PHOTO_PRESENTATION_CONFIG.photoSlider.brannan?.aspectRatio?.toString()}
          />
        );
      }
      case 'photoTrio': {
        return <PhotoTrio photos={headerPhoto?.photos?.map(photo => ({ ...photo, id: photo.id, photoUrl: photo.url })) || []} />;
      }
      default:
        return null;
    }
  }, [headerPhoto, pageId, pageSlug, pageType, handleEditPhotoClick]);

  return getHeaderPhotoComponent();
};
